import React, { useState, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
} from '@tanstack/react-table';
import { Settings, UserPlus, UserMinus, X, ArrowUpDown, FileCheck, FileX, Download } from 'lucide-react';
import { __ } from './translations';

const CompanyList = ({
  companies,
  onToggleBlock,
  onDelete,
  onEdit,
  searchTerm,
  pagination,
  setPagination
}) => {
  const [loadingRows, setLoadingRows] = useState({});
  const [sorting, setSorting] = useState([]);


  const handleToggleBlock = async (companyId) => {
    setLoadingRows(prev => ({ ...prev, [companyId]: true }));
    await onToggleBlock(companyId);
    setLoadingRows(prev => ({ ...prev, [companyId]: false }));
  };

  const getMemberTypeLabel = (type) => {
    switch (type) {
      case 'regular':
        return __('Regular');
      case 'supporter':
        return __('Supporter');
      default:
        return type;
    }
  };

  const columns = useMemo(() => [
    {
      header: __('Company Name'),
      accessorKey: 'title.rendered',
      cell: info => info.getValue()
    },
    {
      header: __('NIP'),
      accessorKey: 'meta._bms_nip',
      cell: info => info.getValue()
    },
    {
      header: __('City'),
      accessorKey: 'meta._bms_city',
      cell: info => info.getValue()
    },
    {
      header: __('Member Type'),
      accessorKey: 'meta._bms_member_type',
      cell: info => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          info.getValue() === 'regular' 
            ? 'bg-blue-100 text-blue-800' 
            : 'bg-green-100 text-green-800'
        }`}>
          {getMemberTypeLabel(info.getValue())}
        </span>
      )
    },
    {
      header: __('Resolution'),
      id: 'resolution',
      cell: info => {
        const company = info.row.original;
        return (
          <div className="flex items-center space-x-2">
            {company.resolution_file ? (
              <>
                <FileCheck className="h-5 w-5 text-green-500" />
                <a
                  href={company.resolution_file.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-2 py-1 text-sm text-blue-600 hover:text-blue-800"
                >
                  <Download className="h-4 w-4 mr-1" />
                  {__('Download')}
                </a>
              </>
            ) : (
              <FileX className="h-5 w-5 text-red-500" />
            )}
          </div>
        );
      }
    },
    {
      header: __('Status'),
      accessorKey: 'meta._account_status',
      cell: info => (
        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
          info.getValue() === 'blocked'
            ? 'bg-red-100 text-red-800'
            : 'bg-green-100 text-green-800'
        }`}>
          {info.getValue() === 'blocked' ? __('Blocked') : __('Active')}
        </span>
      )
    },
    {
      id: 'actions',
      header: __('Actions'),
      cell: info => {
        const company = info.row.original;
        const isLoading = loadingRows[company.id];

        return (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onEdit(company)}
              className="text-indigo-600 hover:text-indigo-900"
              disabled={isLoading}
            >
              <Settings className="h-5 w-5" />
            </button>
            <button
              onClick={() => handleToggleBlock(company.id)}
              className="text-yellow-600 hover:text-yellow-900"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-yellow-600 border-t-transparent" />
              ) : company.meta._account_status === 'blocked' ? (
                <UserPlus className="h-5 w-5" />
              ) : (
                <UserMinus className="h-5 w-5" />
              )}
            </button>
            <button
              onClick={() => onDelete(company.id)}
              className="text-red-600 hover:text-red-900"
              disabled={isLoading}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        );
      }
    }
  ], [loadingRows, onToggleBlock, onEdit, onDelete]);

  const table = useReactTable({
    data: companies,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
  //  onPaginationChange: setPagination,
    state: {
      sorting,
      pagination,
      globalFilter: searchTerm,
    },
    //manualPagination: true,
  });


   // Handle pagination changes
   const handlePaginationChange = (updater) => {
    if (typeof updater === 'function') {
      setPagination(updater(pagination));
    } else {
      setPagination(updater);
    }
  };

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto relative">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <div className="flex items-center gap-2">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getCanSort() && (
                        <ArrowUpDown className="h-4 w-4" />
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {table.getRowModel().rows.map(row => (
              <tr key={row.id}>
                {row.getVisibleCells().map(cell => (
                  <td
                    key={cell.id}
                    className="px-6 py-4"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      <div className="flex items-center justify-between px-4">
      <div className="flex items-center gap-2">
          <button
            onClick={() => handlePaginationChange({ ...pagination, pageIndex: 0 })}
            disabled={!table.getCanPreviousPage()}
            className="px-2 py-1 border rounded hover:bg-gray-100 disabled:opacity-50"
          >
            {'<<'}
          </button>
          <button
            onClick={() => handlePaginationChange({ ...pagination, pageIndex: pagination.pageIndex - 1 })}
            disabled={!table.getCanPreviousPage()}
            className="px-2 py-1 border rounded hover:bg-gray-100 disabled:opacity-50"
          >
            {'<'}
          </button>
          <button
            onClick={() => handlePaginationChange({ ...pagination, pageIndex: pagination.pageIndex + 1 })}
            disabled={!table.getCanNextPage()}
            className="px-2 py-1 border rounded hover:bg-gray-100 disabled:opacity-50"
          >
            {'>'}
          </button>
          <button
            onClick={() => handlePaginationChange({ ...pagination, pageIndex: table.getPageCount() - 1 })}
            disabled={!table.getCanNextPage()}
            className="px-2 py-1 border rounded hover:bg-gray-100 disabled:opacity-50"
          >
            {'>>'}
          </button>
        </div>

        <span className="flex items-center gap-1">
          <div>Page</div>
          <strong>
            {pagination.pageIndex + 1} of {table.getPageCount()}
          </strong>
        </span>

        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1">
            {__('Go to page:')}
            <input
              type="number"
              value={pagination.pageIndex + 1}
              onChange={e => {
                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                handlePaginationChange({ ...pagination, pageIndex: page });
              }}
              className="w-16 p-1 border rounded"
            />
          </span>
          <select
            value={pagination.pageSize}
            onChange={e => {
              handlePaginationChange({ 
                ...pagination, 
                pageSize: Number(e.target.value),
                pageIndex: 0 // Reset to first page when changing page size
              });
            }}
            className="p-2 border rounded"
          >
            {[10, 20, 30, 40, 50].map(pageSize => (
              <option key={pageSize} value={pageSize}>
                {__('Show')} {pageSize}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

export default CompanyList;